import { useMemo, useState, type FormEvent } from 'react';
import type {
  AccountDto,
  FiscalPeriodDto,
  PostJournalRequest,
  ProblemDetails,
} from '@hesabat/contracts';
import { api, ProblemError, type PostedJournal } from './api';
import { formatMoney, isZero, parseAmount, sumAmounts } from './money';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  periods: readonly FiscalPeriodDto[];
  onPosted: (journal: PostedJournal) => void;
  onOpenLedger: (accountCode: string) => void;
  /** يبدأ فى وضع قيد الافتتاح — من معالج إعداد الشركة. */
  initialOpening?: boolean;
}

interface LineState {
  /** مفتاح React ثابت للسطر — الفهرس يتغير عند حذف سطر من المنتصف. */
  id: number;
  accountCode: string;
  debit: string;
  credit: string;
  memo: string;
  counterpartyId: string;
  cashboxId: string;
}

type Issue = NonNullable<ProblemDetails['issues']>[number];

const COUNTERPARTY_TYPES = {
  customer: 'customer',
  supplier: 'supplier',
  employee: 'employee',
} as const;

const COUNTERPARTY_LABELS: Record<keyof typeof COUNTERPARTY_TYPES, string> = {
  customer: 'كود العميل',
  supplier: 'كود المورد',
  employee: 'كود الموظف',
};

let nextLineId = 1;

function emptyLine(): LineState {
  return {
    id: nextLineId++,
    accountCode: '',
    debit: '',
    credit: '',
    memo: '',
    counterpartyId: '',
    cashboxId: '',
  };
}

function today(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return now.getFullYear() + '-' + month + '-' + day;
}

/** مسار تحقق العقد (`lines.2.debit`) يُرد إلى رقم سطره ليظهر الخطأ تحته. */
function lineIndexOf(issue: Issue): number | undefined {
  if (issue.lineIndex !== undefined) return issue.lineIndex;
  const match = /^lines\.(\d+)/.exec(issue.path ?? '');
  return match ? Number(match[1]) : undefined;
}

/** أكواد المحرك برسائل يفهمها محاسب — والكود نفسه يبقى ظاهرًا للبلاغ. */
const ISSUE_MESSAGES: Record<string, string> = {
  UNBALANCED_JOURNAL: 'القيد غير متزن: مجموع المدين لا يساوى مجموع الدائن.',
  TOO_FEW_LINES: 'القيد يحتاج سطرين على الأقل.',
  LINE_NO_AMOUNT: 'السطر بلا مبلغ.',
  LINE_BOTH_SIDES: 'السطر يحمل مدينًا أو دائنًا، لا الاثنين.',
  NEGATIVE_AMOUNT: 'المبالغ السالبة غير مسموحة.',
  ACCOUNT_NOT_FOUND: 'الحساب غير موجود فى الدليل.',
  ACCOUNT_NOT_POSTABLE: 'حساب أب — الترحيل على الحسابات الفرعية فقط.',
  ACCOUNT_INACTIVE: 'الحساب موقوف.',
  MISSING_COUNTERPARTY: 'حساب مراقبة: اكتب كود الطرف (العميل أو المورد أو الموظف).',
  MISSING_CASHBOX: 'حساب خزينة: اكتب كود الخزينة.',
  MISSING_TAX_SNAPSHOT: 'حساب ضريبة لا يُرحَّل يدويًا بلا بيانات الضريبة.',
  NEGATIVE_CASHBOX: 'الخزينة ستصبح سالبة بهذا القيد.',
  PERIOD_CLOSED: 'الفترة المالية مقفلة.',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية.',
  OPENING_NON_BS_ACCOUNT: 'قيد الافتتاح لحسابات الميزانية فقط — لا إيرادات ولا مصروفات.',
  OPENING_DATE_NOT_PERIOD_START: 'قيد الافتتاح بتاريخ أول يوم فى السنة المالية.',
};

function describe(issue: Issue): string {
  return ISSUE_MESSAGES[issue.code] ?? issue.message;
}

/**
 * قيد يومية يدوى. يظهر فقط لمن يملك `journal.manual` (المالك والمحاسب)،
 * والـ API يرفض غيرهم على أى حال — الإخفاء هنا راحة لا حماية.
 *
 * المفتاح (`Idempotency-Key`) واحد لكل نموذج: ضغطتان أو إعادة بعد انقطاع
 * الشبكة تُرحّلان **قيدًا واحدًا**. يتجدد المفتاح فقط بعد ترحيل ناجح.
 */
export default function JournalEntry({
  companyId,
  currency,
  accounts,
  periods,
  onPosted,
  onOpenLedger,
  initialOpening = false,
}: Props) {
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [postingDate, setPostingDate] = useState(today);
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<LineState[]>(() => [emptyLine(), emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [posted, setPosted] = useState<PostedJournal | null>(null);
  // قيد الافتتاح: نفس النموذج بقواعد أضيق — ميزانية فقط، وتاريخ أول يوم فى السنة
  const [opening, setOpening] = useState(initialOpening);

  const openPeriods = periods.filter((period) => period.status === 'OPEN');
  const yearStarts = [...new Set(openPeriods.map((period) => period.startDate))].sort();

  // حسابات الضريبة تحتاج لقطة ضريبية بنسختها — مكانها مستند المبيعات لا القيد الحر
  const selectable = useMemo(
    () =>
      accounts.filter(
        (account) =>
          account.isPostable &&
          account.subledger !== 'tax' &&
          (!opening || account.statement === 'BS'),
      ),
    [accounts, opening],
  );
  const byCode = useMemo(
    () => new Map(accounts.map((account) => [account.code, account])),
    [accounts],
  );

  const parsed = lines.map((line) => ({
    debit: parseAmount(line.debit),
    credit: parseAmount(line.credit),
  }));
  const amountsValid = parsed.every((item) => item.debit !== null && item.credit !== null);
  const totalDebit = sumAmounts(parsed.map((item) => item.debit ?? '0'));
  const totalCredit = sumAmounts(parsed.map((item) => item.credit ?? '0'));
  const difference = totalDebit.sub(totalCredit);
  const balanced = amountsValid && difference.isZero() && !totalDebit.isZero();
  const allAccountsChosen = lines.every((line) => line.accountCode !== '');

  function update(id: number, patch: Partial<LineState>): void {
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
    setIssues([]);
  }

  function addLine(): void {
    setLines((current) => [...current, emptyLine()]);
  }

  function removeLine(id: number): void {
    setLines((current) =>
      current.length > 2 ? current.filter((line) => line.id !== id) : current,
    );
    setIssues([]);
  }

  function toggleOpening(next: boolean): void {
    setOpening(next);
    // حمولة مختلفة بنفس المفتاح تُرفض 409 — كل وضع له مفتاحه
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    if (next && yearStarts.length > 0) setPostingDate(yearStarts[yearStarts.length - 1]!);
    if (!next) setPostingDate(today());
  }

  function reset(): void {
    setIdempotencyKey(crypto.randomUUID());
    setLines([emptyLine(), emptyLine()]);
    setReference('');
    setMemo('');
    setIssues([]);
    setFailure(null);
  }

  function buildRequest(): PostJournalRequest {
    return {
      sourceType: 'manual_journal',
      // المصدر مشتق من المفتاح: الإعادة بنفس المفتاح تحمل نفس الحمولة حرفيًا
      sourceId: 'mj-' + idempotencyKey,
      postingDate,
      reference: reference.trim() || null,
      memo: memo.trim() || null,
      lines: lines.map((line, index) => {
        const debit = parsed[index]?.debit ?? '0';
        const credit = parsed[index]?.credit ?? '0';
        const account = byCode.get(line.accountCode);
        const subledger = account?.subledger;
        const counterpartyType =
          subledger && subledger in COUNTERPARTY_TYPES
            ? COUNTERPARTY_TYPES[subledger as keyof typeof COUNTERPARTY_TYPES]
            : null;
        return {
          accountCode: line.accountCode,
          debit,
          credit,
          currency,
          amountCurrency: isZero(debit) ? credit : debit,
          currencyRate: '1',
          counterparty:
            counterpartyType && line.counterpartyId.trim()
              ? { type: counterpartyType, id: line.counterpartyId.trim() }
              : null,
          cashboxId:
            subledger === 'cashbox' && line.cashboxId.trim() ? line.cashboxId.trim() : null,
          memo: line.memo.trim() || null,
        };
      }),
    };
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (submitting || !balanced || !allAccountsChosen) return;

    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    setPosted(null);

    try {
      const request = buildRequest();
      const journal = opening
        ? await api.postOpeningBalance(companyId, idempotencyKey, {
            postingDate: request.postingDate,
            reference: request.reference,
            memo: request.memo,
            lines: request.lines,
          })
        : await api.postJournal(companyId, idempotencyKey, request);
      setPosted(journal);
      onPosted(journal);
      reset();
    } catch (caught) {
      if (caught instanceof ProblemError) {
        const { problem } = caught;
        if (problem.status === 409 && opening && /opening balance/.test(problem.detail ?? '')) {
          setFailure('قيد افتتاح هذه السنة مُرحَّل بالفعل. التصحيح بقيد يومية عادى.');
        } else if (problem.status === 409) {
          setFailure(
            'هذا النموذج أُرسل من قبل ببيانات مختلفة. افتح قيدًا جديدًا وأعد إدخاله — لم يُرحَّل شىء جديد.',
          );
        } else if (problem.status === 403) {
          setFailure('لا تملك صلاحية القيد اليدوى فى هذه الشركة.');
        } else {
          setIssues(problem.issues ?? []);
          if (!problem.issues?.length) setFailure(problem.detail ?? problem.title);
        }
      } else {
        // لا نعرف هل وصل الطلب: المفتاح نفسه باقٍ، فإعادة الضغط لا تُكرر القيد
        setFailure(
          'تعذر الاتصال بالخادم. اضغط «ترحيل» مرة أخرى — لن يتكرر القيد لو كان وصل بالفعل.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const generalIssues = issues.filter((issue) => lineIndexOf(issue) === undefined);

  return (
    <form className="panel journal-form" onSubmit={(event) => void submit(event)} noValidate>
      <h2>{opening ? 'قيد افتتاح' : 'قيد يومية يدوى'}</h2>

      <label className="toggle">
        <input
          type="checkbox"
          checked={opening}
          onChange={(event) => toggleOpening(event.target.checked)}
          disabled={yearStarts.length === 0}
        />
        قيد افتتاح — أرصدة أول السنة لحسابات الميزانية، مرة واحدة لكل سنة
        <Hint>اختره فقط لتسجيل الأرصدة التى كانت عندك يوم بدأت على البرنامج.</Hint>
      </label>

      {posted && (
        <div className="success" role="status">
          {posted.replayed
            ? 'هذا القيد كان مُرحَّلاً بالفعل — لم يُكرَّر.'
            : 'تم ترحيل القيد ' + (posted.number ?? '') + '.'}{' '}
          إجمالى {formatMoney(posted.totalDebit)} {currency}.{' '}
          {posted.lines
            .map((line) => line.accountCode)
            .filter((code, i, all) => all.indexOf(code) === i)
            .slice(0, 3)
            .map((code) => (
              <button key={code} type="button" className="link" onClick={() => onOpenLedger(code)}>
                كشف {code}
              </button>
            ))}
        </div>
      )}

      <div className="form-row">
        <label>
          التاريخ
          {opening ? (
            <select value={postingDate} onChange={(event) => setPostingDate(event.target.value)}>
              {yearStarts.map((start) => (
                <option key={start} value={start}>
                  {start}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="date"
              value={postingDate}
              onChange={(event) => setPostingDate(event.target.value)}
              required
            />
          )}
        </label>
        <label>
          المرجع
          <input
            value={reference}
            maxLength={64}
            onChange={(event) => setReference(event.target.value)}
            placeholder="اختيارى — رقم مستند مثلاً"
          />
          <Hint>رقم المستند الورقى إن وُجد، ليسهل الرجوع إليه.</Hint>
        </label>
        <label className="grow">
          البيان
          <input
            value={memo}
            maxLength={500}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="وصف القيد"
          />
          <Hint>جملة تشرح سبب القيد، مثل «إيجار المحل عن شهر مارس».</Hint>
        </label>
      </div>

      <div className="table-scroll">
        <table className="journal-lines lines-form">
          <thead>
            <tr>
              <th>#</th>
              <th>الحساب</th>
              <th>مدين</th>
              <th>دائن</th>
              <th>بيان السطر</th>
              <th aria-label="حذف" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const account = byCode.get(line.accountCode);
              const subledger = account?.subledger ?? null;
              const lineIssues = issues.filter((issue) => lineIndexOf(issue) === index);
              const debitInvalid = parsed[index]?.debit === null;
              const creditInvalid = parsed[index]?.credit === null;
              return (
                <tr key={line.id} className={lineIssues.length ? 'has-issue' : undefined}>
                  <td data-label="السطر" className="num-cell">
                    {index + 1}
                  </td>
                  <td data-label="الحساب">
                    <select
                      value={line.accountCode}
                      onChange={(event) => update(line.id, { accountCode: event.target.value })}
                      aria-label={'حساب السطر ' + (index + 1)}
                    >
                      <option value="">— اختر حسابًا —</option>
                      {selectable.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.code} — {item.nameAr}
                        </option>
                      ))}
                    </select>
                    {subledger && subledger in COUNTERPARTY_LABELS && (
                      <input
                        className="sub-input"
                        value={line.counterpartyId}
                        onChange={(event) =>
                          update(line.id, { counterpartyId: event.target.value })
                        }
                        placeholder={
                          COUNTERPARTY_LABELS[subledger as keyof typeof COUNTERPARTY_LABELS]
                        }
                      />
                    )}
                    {subledger === 'cashbox' && (
                      <input
                        className="sub-input"
                        value={line.cashboxId}
                        onChange={(event) => update(line.id, { cashboxId: event.target.value })}
                        placeholder="كود الخزينة"
                      />
                    )}
                    {lineIssues.map((issue, i) => (
                      <p key={i} className="error line-issue">
                        {describe(issue)}
                      </p>
                    ))}
                  </td>
                  <td data-label="مدين">
                    <input
                      className={'amount' + (debitInvalid ? ' invalid' : '')}
                      inputMode="decimal"
                      value={line.debit}
                      onChange={(event) =>
                        update(line.id, {
                          debit: event.target.value,
                          // السطر جانب واحد: الكتابة فى المدين تمسح الدائن
                          ...(event.target.value.trim() ? { credit: '' } : {}),
                        })
                      }
                      aria-label={'مدين السطر ' + (index + 1)}
                      aria-invalid={debitInvalid}
                    />
                  </td>
                  <td data-label="دائن">
                    <input
                      className={'amount' + (creditInvalid ? ' invalid' : '')}
                      inputMode="decimal"
                      value={line.credit}
                      onChange={(event) =>
                        update(line.id, {
                          credit: event.target.value,
                          ...(event.target.value.trim() ? { debit: '' } : {}),
                        })
                      }
                      aria-label={'دائن السطر ' + (index + 1)}
                      aria-invalid={creditInvalid}
                    />
                  </td>
                  <td data-label="بيان السطر">
                    <input
                      value={line.memo}
                      maxLength={500}
                      onChange={(event) => update(line.id, { memo: event.target.value })}
                      aria-label={'بيان السطر ' + (index + 1)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="link"
                      onClick={() => removeLine(line.id)}
                      disabled={lines.length <= 2}
                      aria-label={'حذف السطر ' + (index + 1)}
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td />
              <td>
                <button type="button" className="link" onClick={addLine}>
                  + سطر
                </button>
              </td>
              <td data-label="إجمالى المدين" className="num-cell total">
                {formatMoney(totalDebit)}
              </td>
              <td data-label="إجمالى الدائن" className="num-cell total">
                {formatMoney(totalCredit)}
              </td>
              <td colSpan={2}>
                {!amountsValid ? (
                  <span className="pill bad">مبلغ غير صالح</span>
                ) : difference.isZero() ? (
                  totalDebit.isZero() ? (
                    <span className="label">أدخل المبالغ</span>
                  ) : (
                    <span className="pill">متزن</span>
                  )
                ) : (
                  <span className="pill bad">الفرق {formatMoney(difference.abs())}</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {generalIssues.map((issue, i) => (
        <p key={i} className="error">
          {describe(issue)}
        </p>
      ))}
      {failure && <p className="error">{failure}</p>}

      <div className="form-actions sticky">
        <button
          type="submit"
          className="primary"
          disabled={submitting || !balanced || !allAccountsChosen}
        >
          {submitting ? 'جارى الترحيل…' : 'ترحيل القيد'}
        </button>
        <button type="button" className="ghost" onClick={reset} disabled={submitting}>
          قيد جديد
        </button>
        <span className="label">المبالغ بـ {currency}.</span>
        <Hint block>
          لا يُرحَّل القيد إلا إذا تساوى المدين والدائن. بعد الترحيل لا يُعدَّل ولا يُحذف — الخطأ
          يُصحَّح بمستند عكسى، حتى يبقى تاريخ كل مبلغ واضحًا.
        </Hint>
      </div>
    </form>
  );
}
